<script setup>

import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import { trans } from 'laravel-vue-i18n';
import Slider from "primevue/slider";
import Button from "primevue/button";
import ToggleButton from "primevue/togglebutton";
import ButtonGroup from "primevue/buttongroup";
import FormMockUp from "./FormMockUp.vue";
import FileUploader from "./FileUploader.vue";
import ColorPicker from "primevue/colorpicker";
import InputMask from "primevue/inputmask";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import SelectButton from "primevue/selectbutton";
import Select from "primevue/select";
import ThemePoint from "./settings/ThemePoint.vue";
import ToggleSwitch from "primevue/toggleswitch";
import Popover from "primevue/popover";
import {percentToHex} from "../../../../helpers.js";
import PopoverBody from "./theme/PopoverBody.vue";
import ThemeItem from "./theme/ThemeItem.vue";
import {usePage} from "@inertiajs/vue3";
import {driver} from "driver.js";

const previewStyle = computed(() => ({
    backgroundImage: theme.value.background_image_url ? `url(${theme.value.background_image_url})` : '',
    backgroundColor: `#${theme.value.background_color}`,
}));

const props = defineProps({
    forms: Object,
    thin: Boolean,
    customForm: Array,
    absolute: Boolean,
})

const sourceTheme = defineModel('theme')
const settings = defineModel('settings')

const theme = computed({
    get() {
        if (sourceTheme.value.darkMode) {
            return {
                color: sourceTheme.value.color,
                borderless: sourceTheme.value.borderless,
                transparent: sourceTheme.value.transparent,
                darkMode: sourceTheme.value.darkMode,
                darkModeLock: sourceTheme.value.darkModeLock,
                background_color: sourceTheme.value.background_color_dark ?? sourceTheme.value.background_color,
                background_image_url: sourceTheme.value.background_image_url_dark ?? sourceTheme.value.background_image_url,
                logo_url: sourceTheme.value.logo_url_dark ?? sourceTheme.value.logo_url,
                panel_color: sourceTheme.value.panel_color_dark ?? sourceTheme.value.panel_color,
                panel_transparency: sourceTheme.value.panel_transparency_dark ?? sourceTheme.value.panel_transparency,
                panel_backdrop_blur: sourceTheme.value.panel_backdrop_blur_dark ?? sourceTheme.value.panel_backdrop_blur,
                fontFamily: sourceTheme.value.fontFamily,
                rows: sourceTheme.value.rows,
                columns: sourceTheme.value.columns,
                roundness: sourceTheme.value.roundness,
            }
        }
        return {
            color: sourceTheme.value.color,
            borderless: sourceTheme.value.borderless,
            transparent: sourceTheme.value.transparent,
            darkMode: sourceTheme.value.darkMode,
            darkModeLock: sourceTheme.value.darkModeLock,
            background_color: sourceTheme.value.background_color,
            background_image_url: sourceTheme.value.background_image_url,
            logo_url: sourceTheme.value.logo_url,
            panel_color: sourceTheme.value.panel_color,
            panel_transparency: sourceTheme.value.panel_transparency,
            panel_backdrop_blur: sourceTheme.value.panel_backdrop_blur,
            fontFamily: sourceTheme.value.fontFamily,
            rows: sourceTheme.value.rows,
            columns: sourceTheme.value.columns,
            roundness: sourceTheme.value.roundness,
        }
    },
    set(val) {
        Object.assign(sourceTheme.value, val);
    }
})

const availableColors = [
    'gray',
    'red',
    'orange',
    'yellow',
    'green',
    'teal',
    'blue',
    'indigo',
    'purple',
    'pink',
];

const transparent = [
    { value: false, label: trans('forms.theme_filled_bg'), icon: 'pi pi-circle-fill' },
    { value: true, label: trans('forms.theme_empty_bg'), icon: 'pi pi-circle' },
];
const titlePosition = [
    { value: 'left', icon: 'pi pi-align-left', label: trans('forms.theme_position_left') },
    { value: 'center', icon: 'pi pi-align-center', label: trans('forms.theme_position_center') },
];
const days = [
    { label: trans('common.dow_long.mon'), value: 2 },
    { label: trans('common.dow_long.tue'), value: 3 },
    { label: trans('common.dow_long.wed'), value: 4 },
    { label: trans('common.dow_long.thu'), value: 5 },
    { label: trans('common.dow_long.fri'), value: 6 },
    { label: trans('common.dow_long.sat'), value: 7 },
    { label: trans('common.dow_long.sun'), value: 1 },
];
const setSetting = (key, value) => {
    const darkModeable = [
        'background_color', 'background_image_url', 'logo_url', 'panel_color', 'panel_transparency', 'panel_backdrop_blur'
    ]
    if (darkModeable.includes(key) && sourceTheme.value.darkMode)
        key += '_dark';

    sourceTheme.value[key] = value;
}

watch(() => sourceTheme.value.darkMode, (val) => {
    if (val) {
        document.querySelector('html')?.classList.add('dark');
    } else {
        document.querySelector('html')?.classList.remove('dark');
    }
}, {immediate: true})

onUnmounted(() => {
    if (!window.matchMedia) {
        return;
    }
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    if (query.matches) {
        document.querySelector('html')?.classList.add('dark');
    } else {
        document.querySelector('html')?.classList.remove('dark');
    }
})
const expanded = ref(false);

const panel = ref();
const calendar = ref();
const roundness = ref();
const background = ref();
const togglePanel = ($event) => panel.value.toggle($event);
const toggleCalendar = ($event) => calendar.value.toggle($event);
const toggleRoundness = ($event) => roundness.value.toggle($event);
const toggleBackground = ($event) => background.value.toggle($event);

const isFeatureAvailable = (feature) => {
    return usePage().props.user.features.filter(el => el.feature_name === feature).length
}
</script>

<template>
    <div>
        <div class=" dark:border-gray-800 overflow-hidden flex flex-col" :class="thin ? '' : 'border my-n2 rounded-lg'">
            <div class="dark:border-gray-800 md:p-4 bg-center bg-cover"
                 :class="thin ? 'h-dvh overflow-y-scroll' : ''" :style="previewStyle">
                <Popover ref="background">
                    <popover-body :name="$t('forms.theme_background')" icon="pi pi-file" :theme="sourceTheme">
                        <template #illustration>
                            <i class="pi pi-file absolute rotate-12 -top-3 -right-3 text-roori-blue"
                               style="font-size: 5rem"></i>
                        </template>
                        <template #default>
                            <theme-item icon="pi pi-image" :name="$t('forms.theme_image')" :theme="sourceTheme" feature="roori_form_background_image">
                                <template #dark>
                                    <file-uploader v-model:src="sourceTheme.background_image_url_dark"></file-uploader>
                                </template>
                                <template #light>
                                    <file-uploader v-model:src="sourceTheme.background_image_url"></file-uploader>
                                </template>
                            </theme-item>
                            <theme-item icon="pi pi-palette" :name="$t('forms.theme_color')" :theme="sourceTheme" feature="roori_form_background_color">
                                <template #dark>
                                    <div class="flex gap-2 items-center">
                                        <div class="border rounded-lg overflow-hidden">
                                            <ColorPicker v-model="sourceTheme.background_color_dark" format="hex"></ColorPicker>
                                        </div>
                                        <div style="width: 110px">
                                            <IconField>
                                                <InputIcon class="pi pi-hashtag"></InputIcon>
                                                <InputMask v-model="sourceTheme.background_color_dark" mask="******" fluid
                                                           class="w-full"></InputMask>
                                            </IconField>
                                        </div>
                                    </div>
                                </template>
                                <template #light>
                                    <div class="flex gap-2 items-center">
                                        <div class="border rounded-lg overflow-hidden">
                                            <ColorPicker v-model="sourceTheme.background_color" format="hex"></ColorPicker>
                                        </div>
                                        <div style="width: 110px">
                                            <IconField>
                                                <InputIcon class="pi pi-hashtag"></InputIcon>
                                                <InputMask v-model="sourceTheme.background_color" mask="******" fluid
                                                           class="w-full"></InputMask>
                                            </IconField>
                                        </div>
                                    </div>
                                </template>
                            </theme-item>
                        </template>
                    </popover-body>
                </Popover>
                <div class="flex items-center flex-col justify-center relative grow">
                    <div class="flex items-center justify-center pt-24 md:pt-4" id="form-logo">
                        <file-uploader v-if="theme.darkMode" v-model:src="sourceTheme.logo_url_dark"
                                       interactive></file-uploader>
                        <file-uploader v-else v-model:src="sourceTheme.logo_url" interactive></file-uploader>
                    </div>
                    <div class="top-4 left-4 z-50" :class="absolute ? 'absolute' : 'fixed'" id="form-background">
                        <Button icon="pi pi-file" @click="toggleBackground" class="pulsate-fwd" style="height: 36px"
                                severity="secondary" raised rounded :label="$t('forms.theme_background')"></Button>
                    </div>
                    <div class="top-4 right-4 flex gap-4 z-50" :class="absolute ? 'absolute' : 'fixed'" id="form-dark-mode">
                        <ButtonGroup>
                            <Button rounded :disabled="theme.darkModeLock"
                                    :severity="theme.darkMode ? 'contrast' : 'secondary'"
                                    :icon="theme.darkMode ? 'pi pi-moon' : 'pi pi-sun'"
                                    :label="theme.darkMode ? $t('forms.theme_mode_dark') : $t('forms.theme_mode_light')"
                                    style="height: 36px" raised
                                    @click="sourceTheme.darkMode = !sourceTheme.darkMode"></Button>
                            <Button rounded :severity="theme.darkMode ? 'secondary' : 'contrast'"
                                    style="height: 36px" raised
                                    :icon="theme.darkModeLock ? 'pi pi-lock' : 'pi pi-lock-open'"
                                    @click="sourceTheme.darkModeLock = !sourceTheme.darkModeLock"></Button>
                        </ButtonGroup>
                        <slot name="button"></slot>
                    </div>
                    <div class="transform-gpu" id="form-panel">
                        <form-mock-up :forms="forms" :settings="settings" :custom-form="customForm" :theme="theme">
                            <template #panel>
                                <Button icon="pi pi-expand" @click="togglePanel" class="pulsate-fwd" style="height: 36px"
                                        severity="secondary" raised rounded :label="$t('forms.theme_panel_bg')"></Button>
                                <Popover ref="panel">
                                    <popover-body :name="$t('forms.theme_panel_bg')" icon="pi pi-expand" :theme="sourceTheme">
                                        <template #illustration>
                                            <div :class="[{
                                                            'rounded-t': sourceTheme.roundness === 1,
                                                            'rounded-t-lg': sourceTheme.roundness === 2,
                                                            'rounded-t-xl': sourceTheme.roundness === 3
                                                            }]"
                                                 class="border-blue-400 dark:border-white right-0 top-10 border-2 -rotate-45 absolute p-9">
                                            </div>
                                        </template>
                                        <template #default>
                                            <theme-item :name="$t('forms.theme_transparency')" icon="pi pi-image" :theme="sourceTheme" feature="roori_form_panel_transparency">
                                                <template #dark>
                                                    <Slider v-model="sourceTheme.panel_transparency_dark"
                                                            class="grow ml-4" :step="25"></Slider>
                                                </template>
                                                <template #light>
                                                    <Slider v-model="sourceTheme.panel_transparency" class="grow ml-4"
                                                            :step="25"></Slider>
                                                </template>
                                            </theme-item>
                                            <theme-item :name="$t('forms.theme_blur')" icon="pi pi-qrcode" :theme="sourceTheme" feature="roori_form_panel_blur">
                                                <template #dark>
                                                    <ToggleSwitch v-model="sourceTheme.panel_backdrop_blur_dark"
                                                                  :disabled="sourceTheme.panel_transparency_dark === 100"></ToggleSwitch>
                                                </template>
                                                <template #light>
                                                    <ToggleSwitch v-model="sourceTheme.panel_backdrop_blur"
                                                                  :disabled="sourceTheme.panel_transparency === 100"></ToggleSwitch>
                                                </template>
                                            </theme-item>
                                            <theme-item :name="$t('forms.theme_color')" icon="pi pi-palette" :theme="sourceTheme" feature="roori_form_panel_color">
                                                <template #light>
                                                    <div class="border rounded-lg">
                                                        <ColorPicker v-model="sourceTheme.panel_color"
                                                                     format="hex">
                                                        </ColorPicker>
                                                    </div>
                                                    <IconField style="width: 120px">
                                                        <InputIcon class="pi pi-hashtag"></InputIcon>
                                                        <InputMask v-model="sourceTheme.panel_color" fluid
                                                                   class="w-full" mask="******"></InputMask>
                                                    </IconField>
                                                </template>
                                                <template #dark>
                                                    <div class="border rounded-lg">
                                                        <ColorPicker v-model="sourceTheme.panel_color_dark"
                                                                     format="hex">
                                                        </ColorPicker>
                                                    </div>
                                                    <IconField style="width: 120px">
                                                        <InputIcon class="pi pi-hashtag"></InputIcon>
                                                        <InputMask v-model="sourceTheme.panel_color_dark" fluid
                                                                   class="w-full" mask="******"></InputMask>
                                                    </IconField>
                                                </template>
                                            </theme-item>
                                        </template>
                                    </popover-body>
                                </Popover>
                            </template>
                            <template #calendar>
                                <Button icon="pi pi-calendar" :disabled="!isFeatureAvailable('roori_form_calendar')" v-tooltip="!isFeatureAvailable('roori_form_calendar') ? $t('common.feature_plan_available', { version: 'Pro' }) : ''" @click="toggleCalendar" class="pulsate-fwd" style="height: 36px"
                                        severity="secondary" raised rounded :label="$t('forms.theme_calendar_settings')"></Button>
                                <Popover ref="calendar">
                                    <popover-body icon="pi pi-calendar" :name="$t('forms.theme_calendar_settings')" singular-theme
                                                  :theme="sourceTheme" feature="roori_form_calendar">
                                        <template #illustration>
                                            <i class="pi pi-calendar absolute rotate-12 -top-3 -right-3 text-roori-blue"
                                               style="font-size: 5rem"></i>
                                        </template>
                                        <template #default>
                                            <theme-item :name="$t('forms.theme_filled_bg')" icon="pi pi-calendar" feature="roori_form_calendar">
                                                <ToggleSwitch v-model="sourceTheme.borderless" :false-value="true"
                                                              :true-value="false"></ToggleSwitch>
                                            </theme-item>
                                            <theme-item :name="$t('forms.theme_month_position')" icon="pi pi-align-center" feature="roori_form_calendar">
                                                <SelectButton v-model="settings.titlePosition" :options="titlePosition"
                                                              optionLabel="value" optionValue="value" dataKey="value"
                                                              aria-labelledby="custom">
                                                    <template #option="slotProps">
                                                        <i :class="slotProps.option.icon"></i>
                                                    </template>
                                                </SelectButton>
                                            </theme-item>
                                            <theme-item :name="$t('forms.theme_first_day')" icon="pi pi-sun" feature="roori_form_calendar">
                                                <Select v-model="settings.firstDayOfWeek" :options="days"
                                                        optionLabel="label"
                                                        optionValue="value"></Select>
                                            </theme-item>
                                            <theme-item :name="$t('forms.theme_week_numbers')" icon="pi pi-list-check" feature="roori_form_calendar">
                                                <ToggleSwitch v-model="settings.showWeekNumbers"></ToggleSwitch>
                                            </theme-item>
                                        </template>
                                    </popover-body>
                                </Popover>
                            </template>
                            <template #roundness>
                                <Button icon="pi pi-list-check" :label="$t('forms.theme_panel_settings')" @click="toggleRoundness"
                                        class="pulsate-fwd" style="height: 36px"
                                        severity="secondary" raised rounded></Button>
                                <Popover ref="roundness">
                                    <popover-body icon="pi pi-list-check" singular-theme :name="$t('forms.theme_panel_settings')"
                                                  :theme="sourceTheme">
                                        <template #illustration>
                                            <i class="pi pi-list-check absolute rotate-12 -top-3 -right-3 text-roori-blue"
                                               style="font-size: 5rem"></i>
                                        </template>
                                        <template #default>
                                            <theme-item :name="$t('forms.theme_roundness')" icon="pi pi-circle" :theme="sourceTheme" feature="roori_form_panel_roundness">
                                                <div class="grid grid-cols-4 gap-2 justify-center">
                                                    <Button v-for="n in 4" :key="n"
                                                            class=" dark:bg-gray-900"
                                                            size="small"
                                                            style="width: 30px"
                                                            :severity="theme.roundness === n - 1 ? 'contrast' : 'secondary'"
                                                            @click="setSetting('roundness', n-1)">
                                                        <template #icon>
                                                            <div class="border-2 p-1.5 "
                                                                 :class="[theme.roundness === n - 1 ? 'border-gray-200' : 'border-gray-500', {'rounded': n > 1, 'rounded-lg': n === 3, 'rounded-xl': n === 4}]"></div>
                                                        </template>
                                                    </Button>
                                                </div>
                                            </theme-item>
                                            <theme-item :name="$t('forms.theme_accent')" icon="pi pi-palette" :theme="sourceTheme" feature="roori_form_panel_accent">
                                                <div class="grid grid-cols-5 gap-y-1 gap-x-1 justify-center">
                                                    <div v-for="color in availableColors"
                                                         class="border dark:border-gray-800 rounded cursor-pointer opacity-90 hover:opacity-70 transition-all flex items-center justify-center text-white p-1"
                                                         @click="setSetting('color', color)"
                                                         style="width: 25px; height: 25px"
                                                         :class="`bg-${color}-500 ${theme.color === color ? 'opacity-100 border-2 shadow':''}`">
                                                    </div>
                                                </div>
                                            </theme-item>
                                        </template>
                                    </popover-body>
                                </Popover>
                            </template>
                        </form-mock-up>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.pulsate-fwd {
    -webkit-animation: pulsate-fwd 1.5s ease-in-out 30ms both;
    animation: pulsate-fwd 1.5s ease-in-out 30ms both;
    animation-delay: 3s;
}

@-webkit-keyframes pulsate-fwd {
    0% {
        -webkit-transform: scale(1);
        transform: scale(1)
    }
    50% {
        -webkit-transform: scale(1.1);
        transform: scale(1.1)
    }
    100% {
        -webkit-transform: scale(1);
        transform: scale(1)
    }
}

@keyframes pulsate-fwd {
    0% {
        -webkit-transform: scale(1);
        transform: scale(1)
    }
    50% {
        -webkit-transform: scale(1.1);
        transform: scale(1.1)
    }
    100% {
        -webkit-transform: scale(1);
        transform: scale(1)
    }
}
</style>
